import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PrintIcon from '@mui/icons-material/Print';
import SendIcon from '@mui/icons-material/Send';
import CancelIcon from '@mui/icons-material/Cancel';
import LockIcon from '@mui/icons-material/Lock';
import EditIcon from '@mui/icons-material/Edit';
import {
  DialogContainer,
  DialogTitle,
  DialogBody,
  DialogActions,
  GeneralTableBox,
  HeadTableCell,
  NoDataSkeleton,
  RowTableCell,
  StatusChip,
  TableBodySkeleton,
} from '../../../common';
import SupplyOrderStatusChip from './SupplyOrderStatusChip';
import SupplyOrderDestinationSummary from './SupplyOrderDestinationSummary';
import SupplyOrderActionDialog from './SupplyOrderActionDialog';
import ReceiveSupplyOrderDialog from './ReceiveSupplyOrderDialog';
import IssueSupplyOrderDialog from './IssueSupplyOrderDialog';
import {
  canAcknowledgeSupplyOrder,
  canCancelSupplyOrder,
  canCloseSupplyOrder,
  canEditSupplyOrder,
  canIssueSupplyOrder,
  canMarkSupplyOrderSent,
  canPrintSupplyOrder,
  canReceiveSupplyOrder,
  formatSupplyOrderDate,
  formatSupplyOrderQuantity,
  formatSourceType,
  getSupplyOrderApiErrorMessage,
  getSupplyOrderTotal,
} from '../utils/supplyOrdersUtils';
import {
  SUPPLY_ORDER_ITEM_TABLE_TEXTS as ITEM_TEXTS,
  SUPPLY_ORDER_PRICE_TEXTS,
  SUPPLY_ORDER_RECEIPT_STATUS_CONFIG,
  SUPPLY_ORDER_RECEIPT_TABLE_TEXTS as RECEIPT_TEXTS,
} from '../constants/supplyOrdersConstants';
import { formatSupplyOrderPrice } from '../utils/formatSupplyOrderPrice';
import { formatSupplyOrderReceiptItems } from '../utils/formatSupplyOrderReceiptItems';
import { usePermissions } from '../../../hooks/usePermissions';
import { guardDialogClose } from '../../utils/actionLockUtils';

import ClientPaginatedTable from '../../../common/components/table/ClientPaginatedTable';
const textSx = { textAlign: 'left' };
const numberSx = { textAlign: 'right' };
const ITEMS_MIN_TABLE_WIDTH = 1080;
const RECEIPTS_MIN_TABLE_WIDTH = 760;

const ITEM_COLUMNS = [
  { label: ITEM_TEXTS.article, sx: textSx },
  { label: ITEM_TEXTS.requested, sx: { ...numberSx, width: 110 } },
  { label: ITEM_TEXTS.received, sx: { ...numberSx, width: 110 } },
  { label: ITEM_TEXTS.pending, sx: { ...numberSx, width: 110 } },
  { label: ITEM_TEXTS.cancelled, sx: { ...numberSx, width: 110 } },
  { label: ITEM_TEXTS.closedDifference, sx: { ...numberSx, width: 120 } },
  {
    label: SUPPLY_ORDER_PRICE_TEXTS.header,
    title: SUPPLY_ORDER_PRICE_TEXTS.tooltip,
    sx: { ...numberSx, width: 150 },
  },
  { label: SUPPLY_ORDER_PRICE_TEXTS.lineTotal, sx: { ...numberSx, width: 150 } },
];

const RECEIPT_COLUMNS = [
  { label: RECEIPT_TEXTS.code, sx: { ...textSx, width: 150 } },
  { label: RECEIPT_TEXTS.date, sx: { ...textSx, width: 120 } },
  { label: RECEIPT_TEXTS.document, sx: { ...textSx, width: 200 } },
  { label: RECEIPT_TEXTS.status, sx: { width: 140 } },
  { label: RECEIPT_TEXTS.items, sx: textSx },
];

const SupplyOrderDetailDialog = ({
  open,
  onClose,
  order,
  unvaluedStock = [],
  isLoading = false,
  onEdit,
  onIssue,
  onMarkSent,
  onAcknowledge,
  onReceive,
  onCancel,
  onCloseOrder,
  onPrint,
  providers = [],
  isProcessing = false,
}) => {
  const [resultMessage, setResultMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [actionDialog, setActionDialog] = useState(null);
  const [receiveDialogOpen, setReceiveDialogOpen] = useState(false);
  const [issueDialogOpen, setIssueDialogOpen] = useState(false);
  const { permissions } = usePermissions();
  const canManageSupplyOrders = permissions.canManageSupplyOrders;
  const canReceiveSupplyOrders = permissions.canReceiveSupplyOrders;
  const isActionLocked = isProcessing;

  useEffect(() => {
    if (open) {
      setResultMessage('');
      setErrorMessage('');
      setActionDialog(null);
      setReceiveDialogOpen(false);
      setIssueDialogOpen(false);
    }
  }, [open, order?.uuid]);

  const receipts = useMemo(() => order?.receipts || [], [order?.receipts]);
  const orderTotal = getSupplyOrderTotal(order?.items || []);

  const runAction = async (actionFn, successMessage) => {
    try {
      setErrorMessage('');
      await actionFn();
      setResultMessage(successMessage);
    } catch (error) {
      setErrorMessage(getSupplyOrderApiErrorMessage(error));
    }
  };

  const handleIssueClick = () => {
    if (isActionLocked) return;
    if (order.source_type === 'provider') {
      setIssueDialogOpen(true);
      return;
    }
    runAction(
      () => onIssue({ id: order.uuid }),
      'Orden emitida correctamente.'
    );
  };

  const handleIssueConfirm = async (payload) => {
    const result = await onIssue({ id: order.uuid, data: payload });
    const issuedCount = Array.isArray(result) ? result.length : 1;
    setResultMessage(
      issuedCount > 1
        ? `Se emitieron ${issuedCount} órdenes correctamente.`
        : 'Orden emitida correctamente.'
    );
  };

  const handleAcknowledge = () =>
    runAction(() => onAcknowledge(order.uuid), 'Orden confirmada correctamente.');

  const handlePrint = async () => {
    try {
      setErrorMessage('');
      await onPrint(order.uuid);
      setResultMessage('PDF generado correctamente.');
    } catch (error) {
      setErrorMessage(getSupplyOrderApiErrorMessage(error, 'No se pudo generar el PDF'));
    }
  };

  if (!open) return null;

  if (isLoading || !order) {
    return (
      <DialogContainer
        open={open}
        onClose={guardDialogClose(isActionLocked, onClose)}
        size="large"
        minHeight="60vh"
      >
        <DialogTitle>Detalle de la orden</DialogTitle>
        <DialogBody>
          <TableBodySkeleton />
        </DialogBody>
        <DialogActions
          handleClose={onClose}
          handleSave={() => {}}
          saveButtonProps={{ sx: { display: 'none' } }}
          cancelLabel="Cerrar"
        />
      </DialogContainer>
    );
  }

  return (
    <>
      <DialogContainer
        open={open}
        onClose={guardDialogClose(isActionLocked, onClose)}
        size="large"
        minHeight="60vh"
      >
        <DialogTitle>
          Orden {order.order_code || 'borrador'}
        </DialogTitle>

        <DialogBody>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
            {resultMessage && <Alert severity="success">{resultMessage}</Alert>}

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              <SupplyOrderStatusChip status={order.status} />
              <Typography variant="body2" color="text.secondary">
                Tipo: {formatSourceType(order.source_type)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Origen: {order.source_label}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Destino: {order.destination_label}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Fecha: {formatSupplyOrderDate(order.order_date)}
              </Typography>
              {order.source_type === 'provider' && order.provider_delivery_commitment_at && (
                <Typography variant="body2" color="text.secondary">
                  Compromiso proveedor: {formatSupplyOrderDate(order.provider_delivery_commitment_at)}
                </Typography>
              )}
              {order.external_order_label && order.external_order_label !== '-' && (
                <Typography variant="body2" color="text.secondary">
                  Pedido técnico: {order.external_order_label}
                </Typography>
              )}
            </Box>

            <SupplyOrderDestinationSummary order={order} />

            {(order.conditions || order.observations) && (
              <Box>
                {order.conditions && (
                  <Typography variant="body2" color="text.secondary">
                    Condiciones: {order.conditions}
                  </Typography>
                )}
                {order.observations && (
                  <Typography variant="body2" color="text.secondary">
                    Observaciones: {order.observations}
                  </Typography>
                )}
              </Box>
            )}

            <Typography variant="subtitle1" fontWeight={700}>
              Artículos
            </Typography>

            <ClientPaginatedTable rows={order.items || []} resetKey={order.uuid}>
              {(pageRows) => (
                <GeneralTableBox
                  hasMaxHeight={false}
                  fixedLayout
                  minTableWidth={ITEMS_MIN_TABLE_WIDTH}
                >
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        {ITEM_COLUMNS.map((column) => (
                          <HeadTableCell key={column.label} sx={column.sx} title={column.title}>
                            {column.label}
                          </HeadTableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(order.items || []).length === 0 ? (
                        <NoDataSkeleton
                          colSpan={ITEM_COLUMNS.length}
                          message={ITEM_TEXTS.empty}
                          showSubtitle={false}
                        />
                      ) : (
                        pageRows.map((item) => (
                          <TableRow key={item.uuid} hover>
                            <RowTableCell sx={textSx} title={item.article_name_snapshot}>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 700, lineHeight: 1.25 }}
                                noWrap
                              >
                                {item.article_name_snapshot}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
                                {item.article_code_snapshot}
                              </Typography>
                              {item.origin && (
                                <Typography
                                  variant="caption"
                                  component="div"
                                  title={item.origin.label}
                                >
                                  {item.origin.order_number ? `${item.origin.order_number} · ` : ''}
                                  {item.origin.label}
                                </Typography>
                              )}
                            </RowTableCell>
                            <RowTableCell sx={numberSx}>
                              {formatSupplyOrderQuantity(item.requested_quantity)}
                            </RowTableCell>
                            <RowTableCell sx={numberSx}>
                              {formatSupplyOrderQuantity(item.received_quantity)}
                            </RowTableCell>
                            <RowTableCell sx={numberSx}>
                              {formatSupplyOrderQuantity(item.pending_quantity)}
                            </RowTableCell>
                            <RowTableCell sx={numberSx}>
                              {formatSupplyOrderQuantity(item.cancelled_quantity)}
                            </RowTableCell>
                            <RowTableCell sx={numberSx}>
                              {formatSupplyOrderQuantity(item.closed_difference_quantity)}
                            </RowTableCell>
                            <RowTableCell sx={numberSx}>
                              {formatSupplyOrderPrice(item.unit_price)}
                            </RowTableCell>
                            <RowTableCell sx={numberSx}>
                              {formatSupplyOrderPrice(item.line_total)}
                            </RowTableCell>
                          </TableRow>
                        ))
                      )}
                      {(order.items || []).length > 0 && (
                        <TableRow>
                          <RowTableCell
                            colSpan={ITEM_COLUMNS.length - 1}
                            sx={{ ...numberSx, fontWeight: 800 }}
                          >
                            {SUPPLY_ORDER_PRICE_TEXTS.orderTotal}
                          </RowTableCell>
                          <RowTableCell sx={{ ...numberSx, fontWeight: 800 }}>
                            {formatSupplyOrderPrice(orderTotal)}
                          </RowTableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </GeneralTableBox>
              )}
            </ClientPaginatedTable>

            {receipts.length > 0 && (
              <>
                <Typography variant="subtitle1" fontWeight={700}>
                  Historial de recepciones
                </Typography>
                <ClientPaginatedTable rows={receipts} resetKey={order.uuid}>
                  {(pageRows) => (
                    <GeneralTableBox
                      hasMaxHeight={false}
                      fixedLayout
                      minTableWidth={RECEIPTS_MIN_TABLE_WIDTH}
                    >
                      <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow>
                            {RECEIPT_COLUMNS.map((column) => (
                              <HeadTableCell key={column.label} sx={column.sx}>
                                {column.label}
                              </HeadTableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {pageRows.map((receipt) => {
                            const statusConfig = SUPPLY_ORDER_RECEIPT_STATUS_CONFIG[receipt.status];
                            const documentLabel = [
                              receipt.supplier_document_type,
                              receipt.supplier_document_number,
                            ]
                              .filter(Boolean)
                              .join(' ');
                            const itemsLabel = formatSupplyOrderReceiptItems(receipt.items);
                            return (
                              <TableRow key={receipt.uuid} hover>
                                <RowTableCell sx={{ ...textSx, fontWeight: 700 }}>
                                  {receipt.receipt_code}
                                </RowTableCell>
                                <RowTableCell sx={textSx}>
                                  {formatSupplyOrderDate(receipt.received_at)}
                                </RowTableCell>
                                <RowTableCell sx={textSx} title={documentLabel}>
                                  {documentLabel || '-'}
                                </RowTableCell>
                                <RowTableCell>
                                  <StatusChip
                                    size="small"
                                    color={statusConfig?.color}
                                    label={statusConfig?.label || receipt.status}
                                  />
                                </RowTableCell>
                                <RowTableCell sx={textSx} title={itemsLabel}>
                                  {itemsLabel}
                                </RowTableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </GeneralTableBox>
                  )}
                </ClientPaginatedTable>
              </>
            )}

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {canManageSupplyOrders && canEditSupplyOrder(order) && onEdit && (
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => onEdit(order)}
                  disabled={isActionLocked}
                >
                  Editar
                </Button>
              )}
              {canManageSupplyOrders && canIssueSupplyOrder(order) && (
                <Button
                  variant="contained"
                  startIcon={<CheckCircleIcon />}
                  onClick={handleIssueClick}
                  disabled={isActionLocked}
                >
                  Emitir
                </Button>
              )}
              {canManageSupplyOrders && canPrintSupplyOrder(order) && (
                <Button
                  variant="outlined"
                  startIcon={<PrintIcon />}
                  onClick={handlePrint}
                  disabled={isActionLocked}
                >
                  Imprimir PDF
                </Button>
              )}
              {canManageSupplyOrders && canMarkSupplyOrderSent(order) && (
                <Button
                  variant="outlined"
                  startIcon={<SendIcon />}
                  onClick={() => setActionDialog('mark-sent')}
                  disabled={isActionLocked}
                >
                  Marcar enviada
                </Button>
              )}
              {canManageSupplyOrders && canAcknowledgeSupplyOrder(order) && (
                <Button
                  variant="outlined"
                  startIcon={<CheckCircleIcon />}
                  onClick={handleAcknowledge}
                  disabled={isActionLocked}
                >
                  Confirmar
                </Button>
              )}
              {canReceiveSupplyOrders && canReceiveSupplyOrder(order) && (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<LocalShippingIcon />}
                  onClick={() => setReceiveDialogOpen(true)}
                  disabled={isActionLocked}
                >
                  Recibir
                </Button>
              )}
              {canManageSupplyOrders && canCloseSupplyOrder(order) && (
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={<LockIcon />}
                  onClick={() => setActionDialog('close')}
                  disabled={isActionLocked}
                >
                  Cerrar
                </Button>
              )}
              {canManageSupplyOrders && canCancelSupplyOrder(order) && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<CancelIcon />}
                  onClick={() => setActionDialog('cancel')}
                  disabled={isActionLocked}
                >
                  Cancelar
                </Button>
              )}
            </Box>
          </Box>
        </DialogBody>

        <DialogActions
          handleClose={onClose}
          handleSave={() => {}}
          saveButtonProps={{ sx: { display: 'none' } }}
          cancelButtonProps={{ disabled: isActionLocked }}
          cancelLabel="Cerrar"
        />
      </DialogContainer>

      <SupplyOrderActionDialog
        open={actionDialog === 'cancel'}
        onClose={() => setActionDialog(null)}
        title="Cancelar orden"
        description="Esta acción cancelará la orden a proveedor. Indicá el motivo."
        confirmLabel="Cancelar orden"
        requireReason
        isProcessing={isProcessing}
        onConfirm={async ({ reason }) => {
          await onCancel({ id: order.uuid, reason });
          setResultMessage('Orden cancelada correctamente.');
        }}
      />

      <SupplyOrderActionDialog
        open={actionDialog === 'close'}
        onClose={() => setActionDialog(null)}
        title="Cerrar orden"
        description="Se cerrará el saldo pendiente de la orden con diferencia."
        confirmLabel="Cerrar orden"
        requireReason
        isProcessing={isProcessing}
        onConfirm={async ({ reason }) => {
          await onCloseOrder({ id: order.uuid, reason });
          setResultMessage('Orden cerrada correctamente.');
        }}
      />

      <SupplyOrderActionDialog
        open={actionDialog === 'mark-sent'}
        onClose={() => setActionDialog(null)}
        title="Marcar como enviada"
        description="La orden pasará al estado enviada."
        confirmLabel="Marcar enviada"
        requireEmail
        isProcessing={isProcessing}
        onConfirm={async ({ recipient_email }) => {
          await onMarkSent({ id: order.uuid, recipient_email });
          setResultMessage('Orden marcada como enviada.');
        }}
      />

      <ReceiveSupplyOrderDialog
        open={receiveDialogOpen}
        onClose={() => setReceiveDialogOpen(false)}
        order={order}
        unvaluedStock={unvaluedStock}
        onReceive={(payload) => onReceive({ id: order.uuid, data: payload })}
        isProcessing={isProcessing}
      />

      <IssueSupplyOrderDialog
        open={issueDialogOpen}
        onClose={() => setIssueDialogOpen(false)}
        order={order}
        providers={providers}
        onIssue={handleIssueConfirm}
        isProcessing={isProcessing}
      />
    </>
  );
};

export default SupplyOrderDetailDialog;
